import { PropsWithChildren } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ROUTES_PERMISSIONS } from '@/routes/constants/RoutePermissions';
import { ForbiddenRouteException } from '@/routes/RouteExceptions/ForbiddenRouteException';

import { RESOURCE_TYPE } from '../../data';
import { useAuthorization } from '../../hooks';

type RequirePlatformAuthProps = {
  permissions: string[];
};

export const RequirePlatformRouteAuth = ({
  permissions,
  children,
}: PropsWithChildren<RequirePlatformAuthProps>) => {
  const location = useLocation();
  const { hasResourcePermission, isLoading } = useAuthorization({
    resourceType: RESOURCE_TYPE.PLATFORM,
  });

  if (isLoading) return <></>;

  const canAccess = hasResourcePermission(permissions);

  if (canAccess) return <>{children || <Outlet />}</>;

  const isProductionCenters = location.pathname.startsWith('/app/av/sources/production_centers');

  if (isProductionCenters) {
    // Find the first allowed route in ROUTES_PERMISSIONS (avoid redirecting to the same route)
    const firstAllowedEntry = Object.entries(ROUTES_PERMISSIONS).find(([path, perm]) => {
      if (location.pathname.startsWith(path)) return false;

      const perms = Array.isArray(perm) ? perm : [perm];
      return hasResourcePermission(perms);
    });

    if (firstAllowedEntry) {
      const [path] = firstAllowedEntry;
      return <Navigate to={path} replace />;
    }

    throw new ForbiddenRouteException();
  }

  throw new ForbiddenRouteException();
};
